<script setup>
import { computed, watchEffect } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import SettingsLayout from '@/components/settings/SettingsLayout.vue';
import { useSessionGuard } from '@/composables/useSessionGuard';
import { RoleCode } from '@/constants/accounts';

// Personal settings (account menu → Settings): Profile, Password, Teaching
// (faculty only) and Appearance. Each section is a child route rendered on the right.
useSessionGuard();
const emit = defineEmits(['reloadProfile']);

const route = useRoute();
const router = useRouter();
const store = useStore();

// Faculty accounts (teachers, advisers, admins, prefects) get the Teaching
// section; parent, student and superadmin accounts don't.
const NON_FACULTY_ROLE_CODES = [RoleCode.Parent, RoleCode.Student, RoleCode.Superadmin];
const isFaculty = computed(() => {
  const code = store.getters.activeRoleCode;
  return !!code && !NON_FACULTY_ROLE_CODES.includes(code);
});

// /admin/settings/profile → base /admin/settings
const base = computed(() => route.path.replace(/\/settings(\/.*)?$/, '/settings'));

const items = computed(() =>
  [
    { id: 'profile', title: 'Profile', icon: 'mdi-account-outline' },
    { id: 'password', title: 'Password', icon: 'mdi-lock-outline' },
    isFaculty.value && { id: 'teaching', title: 'Teaching', icon: 'mdi-book-open-outline' },
    { id: 'appearance', title: 'Appearance', icon: 'mdi-palette-outline' },
  ]
    .filter(Boolean)
    .map((item) => ({ ...item, to: `${base.value}/${item.id}` })),
);
const activeId = computed(() => route.path.split('/').pop());

// Teaching is only for faculty.
watchEffect(() => {
  if (activeId.value === 'teaching' && !isFaculty.value) {
    router.replace(`${base.value}/profile`);
  }
});
</script>

<template>
  <SettingsLayout
    title="Settings"
    description="Manage your profile and account settings."
    :items="items"
    :active-id="activeId"
  >
    <router-view @reload-profile="emit('reloadProfile')" />
  </SettingsLayout>
</template>
